import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadIcon } from "components/icons/Download";
import { LinkIcon } from "components/icons/LinkIcon";
import { LongArrowRight } from "components/icons/LongArrowRight";
import { JournalEntrySlider } from "components/JournalEntry/JournalEntrySlider";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useShow } from "hooks/useShow";
import { useGetPayrollQuery } from "store/apis/payroll";
import { Payroll } from "types/Models/payroll";
import { pluralize } from "utils/pluralize";

type PayrollSliderProps = {
  payrollId: string;
  open: boolean;
  close: () => void;
  groupId: string;
  entityId: string;
};

export default function PayrollSlider({
  payrollId,
  open,
  close,
  groupId,
  entityId,
}: PayrollSliderProps) {
  const { downloadFile, ...downloadState } = useDownloadFile();
  const jeSlider = useShow();

  const { data: payroll } = useGetPayrollQuery(
    {
      entityId,
      payrollId,
    },
    {
      skip: !entityId || !payrollId,
    }
  );

  const ledgerEntryId = payroll?.ledger_entry;

  if (!payroll) {
    return null;
  }

  return (
    <>
      <Slider.Root open={open} onOpenChange={close}>
        <Slider.Content>
          <Slider.Header>
            <Slider.Title>{payroll.period}</Slider.Title>
            <Slider.Close />
          </Slider.Header>
          <Slider.Body>
            <Accordion.Root
              defaultValue={["PAYROLL_DETAILS", "PAYROLL_FILE"]}
              type="multiple"
              className="t-flex t-flex-col t-gap-4"
            >
              <Accordion.Item value="PAYROLL_DETAILS">
                <Accordion.Trigger>Payroll details</Accordion.Trigger>
                <Accordion.Content className="t-flex t-flex-col t-gap-5">
                  <Accordion.ItemGrid>
                    <Accordion.InfoItem label="Payroll period">
                      {payroll.period}
                    </Accordion.InfoItem>
                    <Accordion.InfoItem label="Pay day">
                      {dayjs(payroll.pay_day_date).format(DISPLAY_DATE)}
                    </Accordion.InfoItem>
                    <Accordion.InfoItem label="Date of import">
                      {dayjs(payroll.created_at).format(DISPLAY_DATE)}
                    </Accordion.InfoItem>
                  </Accordion.ItemGrid>

                  <Divider />

                  <div className="t-flex t-flex-col t-gap-3">
                    <div className="t-text-subtext t-text-text-60">
                      Payroll totals
                    </div>
                    <Accordion.InfoItem label="Gross pay">
                      <AmountSuperScript amount={payroll.gross_pay} />
                    </Accordion.InfoItem>

                    <Accordion.ItemGrid>
                      <Accordion.InfoItem label="Total employer taxes">
                        <AmountSuperScript
                          amount={payroll.total_employer_taxes}
                        />
                      </Accordion.InfoItem>

                      <Accordion.InfoItem label="Total employee taxes">
                        <AmountSuperScript
                          amount={payroll.total_employee_taxes}
                        />
                      </Accordion.InfoItem>

                      <Accordion.InfoItem label="Reimbursement">
                        <AmountSuperScript amount={payroll.reimbursement} />
                      </Accordion.InfoItem>

                      <Accordion.InfoItem label="Deductions">
                        <AmountSuperScript amount={payroll.deductions} />
                      </Accordion.InfoItem>
                    </Accordion.ItemGrid>
                  </div>
                  <Divider color="dark-grey">
                    <div className="t-flex t-items-center t-justify-center t-gap-1 t-text-body-sm t-text-text-60 t-w-40 t-bg-neutral-0 t-rounded-2xl t-p-1">
                      <LinkIcon />
                      Linked journal entry
                    </div>
                  </Divider>

                  <div className="t-bg-neutral-66 t-p-4 t-rounded-lg t-border-neutral-10 t-border-solid t-border t-flex t-flex-col t-gap-3">
                    <div className="t-text-subtext t-text-text-60">
                      Payroll expenses: {payroll.period}
                    </div>
                    <div className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-2">
                      {dayjs(payroll.created_at).format(DISPLAY_DATE)}
                      <span className="t-text-h4 t-text-center t-text-text-30">
                        ·
                      </span>
                      {pluralize(payroll.txns_ids?.length, "entry", "entries")}
                    </div>
                    <div className="t-max-w-fit">
                      <Button
                        onClick={jeSlider.show}
                        customType="link"
                        size="small"
                      >
                        View/Edit
                        <span className="t-ml-1">
                          <LongArrowRight className="t-h-3 t-w-3" />
                        </span>
                      </Button>
                    </div>
                  </div>
                </Accordion.Content>
              </Accordion.Item>

              <Accordion.Item value="PAYROLL_FILE">
                <Accordion.Trigger>Imported Payroll Report</Accordion.Trigger>
                <Accordion.Content>
                  <FilePreviewTag size="small" file={payroll.file} block>
                    <Button
                      customType="ghost_icon"
                      size="small"
                      disabled={downloadState.result.isFetching}
                      isLoading={downloadState.result.isFetching}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile({
                          file_id: payroll.file.uuid,
                          group_id: groupId,
                        });
                      }}
                    >
                      <DownloadIcon color="currentColor" />
                    </Button>
                  </FilePreviewTag>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          </Slider.Body>
        </Slider.Content>
      </Slider.Root>
      <JournalEntrySlider
        ledgerEntryId={ledgerEntryId!}
        isOpen={jeSlider.shown}
        onClose={jeSlider.hide}
      />
    </>
  );
}
